/**
 * The public booking endpoints, wrapped once.
 *
 * Every call returns a normalized shape or throws a `BookingApiError` carrying
 * the Worker's machine-readable code, which the screens turn into a sentence
 * with `bookingErrorKey`. Nothing here ever surfaces a raw Error to the UI.
 */

import { supabase } from '@/lib/supabase';
import type { Locale } from '@/shared/i18n';
import {
  normalizeAvailability,
  normalizeBooking,
  normalizeBookings,
  normalizeCalendar,
  asRecord,
  type Availability,
  type BookingRecord,
  type PublicCalendar,
} from './types';
import type { BookingMessageKey } from './i18n';

export class BookingApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
    this.name = 'BookingApiError';
  }
}

/** Network failure and malformed payloads both look like this. */
const OFFLINE = new BookingApiError(0, 'network_error');

async function readCode(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    const bag = asRecord(body);
    const code = bag?.error ?? bag?.code;
    return typeof code === 'string' ? code : 'request_failed';
  } catch {
    return 'request_failed';
  }
}

async function call(path: string, init?: RequestInit): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(path, init);
  } catch {
    throw OFFLINE;
  }
  if (!response.ok) {
    throw new BookingApiError(response.status, await readCode(response));
  }
  try {
    return (await response.json()) as unknown;
  } catch {
    throw OFFLINE;
  }
}

/** `Authorization` when the visitor happens to be signed in; `{}` otherwise. */
export async function authHeaders(): Promise<Record<string, string>> {
  try {
    const token = (await supabase.auth.getSession()).data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/* ------------------------------------------------------------------- reads */

export async function fetchPublicCalendar(
  slug: string,
  eventSlug?: string,
): Promise<PublicCalendar> {
  const path = eventSlug
    ? `/api/calendar/public/${encodeURIComponent(slug)}/${encodeURIComponent(eventSlug)}`
    : `/api/calendar/public/${encodeURIComponent(slug)}`;
  const calendar = normalizeCalendar(await call(path));
  if (!calendar) throw new BookingApiError(404, 'calendar_not_found');
  return calendar;
}

export async function fetchAvailability(
  slug: string,
  eventTypeId: string,
  from: Date,
  to: Date,
): Promise<Availability> {
  const query = new URLSearchParams({
    eventTypeId,
    from: from.toISOString(),
    to: to.toISOString(),
  });
  const path = `/api/calendar/public/${encodeURIComponent(slug)}/availability?${query.toString()}`;
  const availability = normalizeAvailability(await call(path));
  if (!availability) throw new BookingApiError(502, 'availability_failed');
  return availability;
}

/* ------------------------------------------------------------------ writes */

export interface CreateBookingInput {
  readonly slug: string;
  readonly eventTypeId: string;
  readonly startsAt: string;
  readonly fullName: string;
  readonly email: string;
  readonly phone: string;
  readonly notes: string;
  readonly locale: Locale;
  readonly intakeAnswers?: Record<string, unknown>;
  readonly attendeeCount?: number;
}

export async function createBooking(
  input: CreateBookingInput,
  idempotencyKey: string,
): Promise<BookingRecord> {
  const payload = await call('/api/bookings', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
      ...(await authHeaders()),
    },
    body: JSON.stringify({ ...input, idempotencyKey }),
  });
  const booking = normalizeBooking(payload);
  if (!booking) throw new BookingApiError(502, 'booking_failed');
  return booking;
}

/**
 * Load a booking from its manage token.
 *
 * The link mailed to the client is `/bookings/manage/<token>` and carries no
 * id, so this prefers a token-addressed endpoint and falls back to the
 * id-addressed one when the caller already knows the id (the confirmation
 * screen appends it, so the round trip works even before the Worker ships
 * `GET /api/bookings/manage/:token`).
 */
export async function fetchBookingByToken(
  token: string,
  id?: string | null,
): Promise<BookingRecord> {
  const headers = { 'X-Booking-Token': token, ...(await authHeaders()) };
  if (id) {
    const booking = normalizeBooking(
      await call(
        `/api/bookings/${encodeURIComponent(id)}?token=${encodeURIComponent(token)}`,
        { headers },
      ),
    );
    if (!booking) throw new BookingApiError(404, 'booking_not_found');
    return booking;
  }
  const booking = normalizeBooking(
    await call(`/api/bookings/manage/${encodeURIComponent(token)}`, { headers }),
  );
  if (!booking) throw new BookingApiError(404, 'booking_not_found');
  return booking;
}

export async function cancelBooking(
  id: string,
  token: string | null,
  reason: string,
): Promise<void> {
  await call(`/api/bookings/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'X-Booking-Token': token } : {}),
      ...(await authHeaders()),
    },
    body: JSON.stringify({ ...(token ? { token } : {}), reason }),
  });
}

export async function rescheduleBooking(
  id: string,
  token: string | null,
  startsAt: string,
): Promise<void> {
  await call(`/api/bookings/${encodeURIComponent(id)}/reschedule`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'X-Booking-Token': token } : {}),
      ...(await authHeaders()),
    },
    body: JSON.stringify({ ...(token ? { token } : {}), startsAt }),
  });
}

export async function fetchMyBookings(): Promise<BookingRecord[]> {
  const headers = await authHeaders();
  if (!headers.Authorization) throw new BookingApiError(401, 'session_required');
  return normalizeBookings(await call('/api/bookings/mine', { headers }));
}

/** The "add to calendar" href. A plain link, so the browser downloads it. */
export function icsUrl(id: string, token: string | null): string {
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  return `/api/bookings/${encodeURIComponent(id)}/ics${query}`;
}

/** The client-facing manage URL, matching the one the Worker mails out. */
export function manageUrl(booking: BookingRecord): string | null {
  if (!booking.manageToken) return null;
  return `/bookings/manage/${encodeURIComponent(booking.manageToken)}?id=${encodeURIComponent(booking.id)}`;
}

/* ------------------------------------------------------------------ errors */

/**
 * Worker code -> sentence. Anything unrecognised falls back to a generic
 * message, so a new code from the Worker degrades to prose, never to `[object
 * Object]` or a blank screen.
 */
export function bookingErrorKey(error: unknown): BookingMessageKey {
  const status = error instanceof BookingApiError ? error.status : -1;
  const code = error instanceof BookingApiError ? error.code : '';

  if (code.includes('slot_unavailable') || status === 409)
    return 'calendarUi.slotUnavailable';
  if (code.includes('rate_limited') || status === 429)
    return 'publicBooking.error.rateLimited';
  if (code.includes('account_required'))
    return 'publicBooking.error.accountRequired';
  if (code.includes('session_required'))
    return 'publicBooking.error.sessionRequired';
  if (code.includes('cancellation_cutoff'))
    return 'publicBooking.error.cancellationCutoff';
  if (code.includes('outside_booking_window'))
    return 'publicBooking.error.outsideWindow';
  if (code.includes('outside_availability'))
    return 'publicBooking.error.outsideAvailability';
  if (code.includes('email_mismatch'))
    return 'publicBooking.error.emailMismatch';
  if (code.includes('unauthorized_booking_action') || status === 403)
    return 'publicBooking.error.unauthorized';
  if (code.includes('booking_not_found'))
    return 'publicBooking.error.bookingNotFound';
  if (code.includes('calendar_not_found') || code.includes('event_not_found'))
    return 'calendarUi.publicNotFound';
  if (code === 'network_error') return 'publicBooking.error.network';
  if (status === 404) return 'publicBooking.error.bookingNotFound';
  if (status === 401) return 'publicBooking.error.accountRequired';
  return 'calendarUi.bookingError';
}

/** True when the failure means "reload the slots and try again". */
export function isSlotConflict(error: unknown): boolean {
  return (
    error instanceof BookingApiError &&
    (error.status === 409 || error.code.includes('slot_unavailable'))
  );
}
